import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Sparkles, Settings, LogOut, User, Package, 
  Menu, X, Users, Search, Home, Bell
} from 'lucide-react';
import Footer from './Footer';
import { NotificationInbox } from './NotificationInbox';
import SubscriptionBanner from './SubscriptionBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AppLayout - Shared layout for all pages (logged in and logged out)
 * Provides consistent Header and Footer across the site
 */
export default function AppLayout({ children, showFooter = true }) {
  const { user, isAuthenticated, token, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Check if user is on Pro/Enterprise plan (should not see upgrade buttons)
  const isProPlan = user?.plan_id === 'brand_enterprise' || user?.plan_id === 'influencer_pro';

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            {/* Logo - Clickable only for non-logged-in users */}
            {isAuthenticated ? (
              <div className="flex items-center space-x-2 cursor-default">
                <Sparkles className="w-8 h-8 text-rose-400" />
                <span className="text-2xl font-bold bg-gradient-to-r from-rose-400 to-orange-400 bg-clip-text text-transparent">
                  VirtualFit
                </span>
              </div>
            ) : (
              <Link to={isAuthenticated ? "/dashboard" : "/"} className="flex items-center space-x-2">
                <Sparkles className="w-8 h-8 text-rose-400" />
                <span className="text-2xl font-bold bg-gradient-to-r from-rose-400 to-orange-400 bg-clip-text text-transparent">
                  VirtualFit
                </span>
              </Link>
            )}

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-6">
              {isAuthenticated ? (
                <>
                  {/* Logged-in Navigation */}
                  <Link 
                    to="/dashboard" 
                    className={`flex items-center text-gray-700 hover:text-rose-500 font-medium transition-colors ${location.pathname === '/dashboard' ? 'text-rose-500' : ''}`}
                  >
                    <Home className="w-4 h-4 mr-1" />
                    Dashboard
                  </Link>
                  <Link 
                    to="/search" 
                    className={`flex items-center text-gray-700 hover:text-rose-500 font-medium transition-colors ${location.pathname === '/search' ? 'text-rose-500' : ''}`}
                  >
                    <Search className="w-4 h-4 mr-1" />
                    Discover Brands & Influencers
                  </Link>
                  {(user?.user_type === 'brand' || user?.user_type === 'influencer') && (
                    <Link 
                      to="/collaborations" 
                      className={`flex items-center text-gray-700 hover:text-rose-500 font-medium transition-colors ${location.pathname === '/collaborations' ? 'text-rose-500' : ''}`}
                    >
                      <Users className="w-4 h-4 mr-1" />
                      Collaborations
                    </Link>
                  )}
                  {user?.user_type === 'brand' && (
                    <Link 
                      to="/portfolio" 
                      className={`flex items-center text-gray-700 hover:text-rose-500 font-medium transition-colors ${location.pathname === '/portfolio' ? 'text-rose-500' : ''}`}
                    >
                      <Package className="w-4 h-4 mr-1" />
                      Portfolio
                    </Link>
                  )}
                </>
              ) : (
                <>
                  {/* Logged-out Navigation */}
                  <Link to="/" className="text-gray-700 hover:text-rose-500 font-medium transition-colors">Home</Link>
                  <Link to="/search" className="text-gray-700 hover:text-rose-500 font-medium transition-colors">Discover Brands & Influencers</Link>
                  <Link to="/blog" className="text-gray-700 hover:text-rose-500 font-medium transition-colors">Blog</Link>
                  <Link to="/about" className="text-gray-700 hover:text-rose-500 font-medium transition-colors">About</Link>
                </>
              )}
            </div>

            {/* Right Side Actions */}
            <div className="hidden md:flex items-center space-x-4">
              {isAuthenticated ? (
                <>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-800">{user?.name}</p>
                    <p className="text-xs text-gray-500 capitalize">{user?.user_type}</p>
                  </div>
                  
                  {/* Notification Inbox - Only for brands and influencers */}
                  {(user?.user_type === 'brand' || user?.user_type === 'influencer') && (
                    <NotificationInbox />
                  )}
                  
                  <Link to="/settings" className="p-2 text-gray-600 hover:text-rose-500 rounded-full hover:bg-gray-100" title="Settings">
                    <User className="w-5 h-5" />
                  </Link>
                  <Link to="/subscription" className="p-2 text-gray-600 hover:text-rose-500 rounded-full hover:bg-gray-100" title="Subscription">
                    <Settings className="w-5 h-5" />
                  </Link>
                  <button 
                    onClick={handleLogout} 
                    className="p-2 text-gray-600 hover:text-rose-500 rounded-full hover:bg-gray-100" 
                    title="Logout"
                  >
                    <LogOut className="w-5 h-5" />
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="text-gray-700 hover:text-rose-500 font-medium">Login</Link>
                  <Link
                    to="/subscribe"
                    className="px-6 py-2.5 bg-gradient-to-r from-rose-400 to-orange-400 text-white rounded-full font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-200"
                  >
                    Subscribe Now
                  </Link>
                </>
              )}
            </div>

            {/* Mobile Menu Button + Notification for Mobile */}
            <div className="md:hidden flex items-center space-x-2">
              {/* Mobile Notification Bell - Only for brands and influencers */}
              {isAuthenticated && (user?.user_type === 'brand' || user?.user_type === 'influencer') && (
                <Link 
                  to="/notifications"
                  className="p-2 text-gray-600 hover:text-rose-500 rounded-full hover:bg-gray-100 relative"
                  data-testid="notification-bell-mobile"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Bell className="w-5 h-5" />
                </Link>
              )}
              <button 
                className="p-2" 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 border-t border-gray-200 pt-4">
              <div className="flex flex-col space-y-4">
                {isAuthenticated ? (
                  <>
                    {/* Notification Link for Mobile */}
                    <Link 
                      to="/notifications" 
                      className="text-gray-700 hover:text-rose-500 font-medium" 
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Notifications
                    </Link>
                    <Link to="/dashboard" className="text-gray-700 hover:text-rose-500 font-medium" onClick={() => setMobileMenuOpen(false)}>Dashboard</Link>
                    <Link to="/search" className="text-gray-700 hover:text-rose-500 font-medium" onClick={() => setMobileMenuOpen(false)}>Discover Brands & Influencers</Link>
                    {(user?.user_type === 'brand' || user?.user_type === 'influencer') && (
                      <Link to="/collaborations" className="text-gray-700 hover:text-rose-500 font-medium" onClick={() => setMobileMenuOpen(false)}>Collaborations</Link>
                    )}
                    {user?.user_type === 'brand' && (
                      <Link to="/portfolio" className="text-gray-700 hover:text-rose-500 font-medium" onClick={() => setMobileMenuOpen(false)}>Portfolio</Link>
                    )}
                    <Link to="/settings" className="text-gray-700 hover:text-rose-500 font-medium" onClick={() => setMobileMenuOpen(false)}>Settings</Link>
                    <button onClick={handleLogout} className="text-left text-gray-700 hover:text-rose-500 font-medium">Logout</button>
                  </>
                ) : (
                  <>
                    <Link to="/" className="text-gray-700 hover:text-rose-500 font-medium" onClick={() => setMobileMenuOpen(false)}>Home</Link>
                    <Link to="/search" className="text-gray-700 hover:text-rose-500 font-medium" onClick={() => setMobileMenuOpen(false)}>Discover Brands & Influencers</Link>
                    <Link to="/blog" className="text-gray-700 hover:text-rose-500 font-medium" onClick={() => setMobileMenuOpen(false)}>Blog</Link>
                    <Link to="/about" className="text-gray-700 hover:text-rose-500 font-medium" onClick={() => setMobileMenuOpen(false)}>About</Link>
                    <Link to="/login" className="text-gray-700 hover:text-rose-500 font-medium" onClick={() => setMobileMenuOpen(false)}>Login</Link>
                    <Link to="/subscribe" className="px-6 py-2.5 bg-gradient-to-r from-rose-400 to-orange-400 text-white rounded-full font-semibold text-center" onClick={() => setMobileMenuOpen(false)}>
                      Subscribe Now
                    </Link>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      {showFooter && <Footer isAuthenticated={isAuthenticated} isProPlan={isProPlan} />}
    </div>
  );
}
